import { useThree } from '@react-three/fiber'
import { DepthPass, OutlineEffect } from 'postprocessing'
import { useEffect } from 'react'
import { Object3D } from 'three'

const exclusionRegistry = new Set<Object3D>()
let cacheDirty = true

export function useOutlineExclusion(obj: Object3D | null | undefined) {
  useEffect(() => {
    if (!obj) return
    exclusionRegistry.add(obj)
    cacheDirty = true
    return () => {
      exclusionRegistry.delete(obj)
      cacheDirty = true
    }
  }, [obj])
}

interface OutlineEffectInternal extends OutlineEffect {
  depthPass?: DepthPass
}

export function useOutlineDepthExclusion(
  outlineRef: React.RefObject<OutlineEffectInternal | null>,
) {
  const { scene } = useThree()

  useEffect(() => {
    const effect = outlineRef.current
    if (!effect) return

    const depthPass = effect.depthPass
    if (!depthPass) return

    let cached: Object3D[] = []

    const defaultRender = depthPass.render.bind(depthPass)
    depthPass.render = (...args) => {
      if (cacheDirty) {
        cached = [...exclusionRegistry]
        cacheDirty = false
      }
      for (const m of cached) m.visible = false
      defaultRender(...args)
      for (const m of cached) m.visible = true
    }

    return () => {
      depthPass.render = defaultRender
    }
  }, [scene, outlineRef])
}
